import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Newspaper, Calendar, ChevronRight, Clock, RefreshCcw } from "lucide-react";
import { Link } from "react-router-dom";
import { api, assetUrl } from "../lib/api";

function formatDate(value) {
  if (!value) return "Recently published";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function newsDate(item) {
  return item.published_at || item.created_at || item.updated_at;
}

function excerpt(item) {
  return item.summary || item.content || "Read the full official campus update.";
}

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15 } },
};

const item = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function loadNews() {
    setLoading(true);
    setError("");
    try {
      const data = await api("/api/news?limit=3");
      setNews(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching campus news:", err);
      setError("Campus news could not be loaded from the CMS.");
      setNews([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadNews();
  }, []);

  const displayNews = useMemo(() => news.slice(0, 3), [news]);

  if (loading) {
    return (
      <section id="news" className="relative w-full py-12 sm:py-16 md:py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 overflow-hidden">
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-full mb-5">
              <div className="w-4 h-4 bg-blue-300 rounded mr-2 animate-pulse"></div>
              <div className="w-20 h-4 bg-blue-300 rounded animate-pulse"></div>
            </div>
            <div className="w-full max-w-md h-10 sm:h-12 bg-gray-200 rounded-lg mx-auto animate-pulse mb-4"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[...Array(3)].map((_, idx) => (
              <div key={idx} className="relative bg-white/80 border border-gray-100 rounded-3xl shadow-xl p-6 sm:p-8 animate-pulse">
                <div className="w-10 h-10 bg-gray-200 rounded-2xl mb-6"></div>
                <div className="w-3/4 h-6 bg-gray-200 rounded mb-4"></div>
                <div className="w-1/2 h-4 bg-gray-200 rounded mb-6"></div>
                <div className="space-y-2">
                  <div className="w-full h-4 bg-gray-200 rounded"></div>
                  <div className="w-5/6 h-4 bg-gray-200 rounded"></div>
                  <div className="w-4/5 h-4 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="news" className="relative w-full py-12 sm:py-16 md:py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 overflow-hidden scroll-mt-32">
      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          className="text-center mb-8 sm:mb-12 md:mb-16"
          initial={{ opacity: 0, y: -40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-5">
            <Newspaper className="w-4 h-4 mr-2" />
            Latest Updates
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 leading-tight px-4">
            Campus{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              News
            </span>
          </h2>
          {error ? <p className="mx-auto max-w-2xl rounded-2xl bg-red-50 px-4 py-3 text-sm font-bold text-red-700">{error}</p> : null}
        </motion.div>

        {displayNews.length === 0 ? (
          <div className="mx-auto max-w-2xl rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm">
            <Newspaper className="mx-auto mb-4 h-10 w-10 text-blue-600" />
            <h3 className="text-xl font-black text-slate-950">No published news yet</h3>
            <p className="mt-2 text-slate-600">
              Publish news from the admin News CMS and it will appear here automatically.
            </p>
            <button
              onClick={loadNews}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-blue-700 px-5 py-3 text-sm font-black text-white hover:bg-blue-800"
            >
              <RefreshCcw className="h-4 w-4" /> Refresh News
            </button>
          </div>
        ) : (
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 w-full"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
          >
            <AnimatePresence>
              {displayNews.map((newsItem, index) => {
                const image = assetUrl(newsItem.image_url);
                return (
                  <motion.div
                    key={newsItem.id || newsItem.slug}
                    variants={item}
                    className="relative group w-full"
                    whileHover={{ y: -8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className={`absolute -top-4 -left-4 w-20 h-20 sm:w-24 sm:h-24 ${
                      index === 0 ? 'bg-blue-200' :
                      index === 1 ? 'bg-purple-200' : 'bg-green-200'
                    } rounded-full opacity-20 group-hover:opacity-30 transition-opacity duration-300`} />

                    <div className="relative bg-white/90 backdrop-blur-xl border border-gray-100 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 p-6 sm:p-8 h-full flex flex-col overflow-hidden">
                      {image ? (
                        <img
                          src={image}
                          alt={newsItem.title}
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                          className="-mx-6 -mt-6 mb-5 h-44 w-[calc(100%+3rem)] object-cover sm:-mx-8 sm:-mt-8 sm:w-[calc(100%+4rem)]"
                        />
                      ) : null}

                      <div className={`w-10 h-10 sm:w-12 sm:h-12 ${
                        index === 0 ? 'bg-blue-100 text-blue-600' :
                        index === 1 ? 'bg-purple-100 text-purple-600' : 'bg-green-100 text-green-600'
                      } rounded-2xl flex items-center justify-center mb-4 sm:mb-6`}>
                        <Newspaper className="w-5 h-5 sm:w-6 sm:h-6" />
                      </div>

                      <h3 className="font-bold text-lg sm:text-xl md:text-2xl text-gray-900 mb-3 sm:mb-4 leading-tight group-hover:text-blue-600 transition-colors">
                        {newsItem.title}
                      </h3>

                      <div className="flex items-center text-gray-500 text-xs sm:text-sm mb-4 sm:mb-6">
                        <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{formatDate(newsDate(newsItem))}</span>
                      </div>

                      <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 line-clamp-3 leading-relaxed flex-grow">
                        {excerpt(newsItem)}
                      </p>

                      <Link
                        to={`/news/${newsItem.slug || newsItem.id}`}
                        className="inline-flex items-center justify-between w-full px-4 sm:px-5 py-2.5 sm:py-3 rounded-2xl bg-gray-50 text-gray-700 font-semibold text-sm hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 group/link"
                      >
                        <span>Read Full Story</span>
                        <ChevronRight className="w-4 h-4 flex-shrink-0 group-hover/link:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>
        )}

        <motion.div
          className="text-center mt-12 sm:mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <Link
            to="/news"
            className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold text-sm sm:text-base rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
          >
            <Clock className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            View All News & Updates
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default News;
